/* eslint-disable no-nested-ternary */
import styled from 'styled-components';
import { SkipNavLink as SkipNavLinkBase } from '@reach/skip-nav';
import { injectIntl } from 'react-intl';
import { focusCssRule } from '#/shared/css/focus';
import { ScreenReaderText } from '#/shared/components/Accessibility';
import SecurityIconBase from '#/shared/components/GeneratedIcons/trimet/SafetySecurity';
import MenuIconBase from '#/shared/components/GeneratedIcons/generic/Menu';
import LogoIconBase from '#/shared/components/GeneratedIcons/trimet/LogoIcon';
import FullLogoBase from '#/shared/components/GeneratedIcons/trimet/FullLogo';
import {
  DESKTOP_MIN_BREAK,
  XSPHONE_BREAK,
  NAV_HEIGHT,
  GTM_DATA_ATTRIBUTE,
  Z_INDEX_500,
  SYSTEM_ALERT_BANNER_HEIGHT,
  Z_INDEX_THIRD_TOP,
  XLDESKTOP_BREAK,
} from '#/shared/constants';
import { ClearLink, ClearGatsbyLink } from '#/shared/components/Styled';
import NavbarStripeSvg from '#/shared/components/GeneratedIcons/trimet/NavbarStripe';

export const NavBar = injectIntl(styled.nav.attrs(({ intl }) => ({
  'aria-label': intl.formatMessage({ id: 'navbar.site-navigation' }),
}))`
  touch-action: none;
  display: ${props =>
    props.mobileMode && !props.isHomePage ? 'none' : 'flex'};
  background-color: ${props => props.theme.mainColor};
  justify-content: center;
  width: 100%;
  color: ${props => props.theme.inverseFontColor1};
  height: ${NAV_HEIGHT};
  align-items: center;
  position: fixed;
  right: initial;
  top: ${props =>
    props.alertsBannerVisible ? SYSTEM_ALERT_BANNER_HEIGHT : null};
  z-index: ${Z_INDEX_500};

  @media (min-width: ${DESKTOP_MIN_BREAK}) {
    display: flex;
    background: ${props => props.theme.mainColor};
  }
`);

export const InnerNav = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: stretch;
  height: 100%;
  position: relative;
  touch-action: none;
  justify-content: space-between;
`;

export const NavLogoColumn = styled(ClearGatsbyLink)`
  ${focusCssRule({ type: 'inset' })}

  background-color: transparent;
  flex: 0 0 auto;
  display: flex;
  cursor: ${props => (props.$isHomePage ? 'default' : 'pointer')};
  align-items: center;
  padding: 0;

  &:hover,
  &:focus,
  :active {
    background-color: transparent;
  }
`;

export const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 17px 15px 0;
  z-index: ${Z_INDEX_THIRD_TOP};
  width: 54px;
  height: 54px;
  @media (min-width: ${DESKTOP_MIN_BREAK}) {
    padding: 0 40px 0 15px;
    transform: translateY(0);
    width: 150px;
    height: auto;
  }
  @media (min-width: ${XLDESKTOP_BREAK}) {
    margin-left: 15px;
  }
`;

export const LogoIcon = styled(LogoIconBase).attrs({
  width: '100%',
  height: '100%',
})``;

export const FullLogo = styled(FullLogoBase)`
  width: 100%;
  height: 100%;
`;

export const SkipNavLink = styled(SkipNavLinkBase)`
  position: absolute;
  top: -500em;
  border-radius: ${props => props.theme.buttonRadius};
  &:focus {
    top: 60px;
    left: 15px;
    background-color: ${props => props.theme.focusVisible};
    padding: 10px;
  }
  @media (min-width: ${XSPHONE_BREAK}) {
    &:focus {
      top: 10px;
      left: 200px;
    }
  }
  background-color: ${props => props.theme.focusVisible};
  color: ${props => props.theme.primaryFontColorReverse};
  text-decoration: none;
`;

export const NavSpacer = styled.div`
  flex: 1 1 auto;
  display: block;
`;

export const NavToggleButtonText = styled(ScreenReaderText).attrs({
  visibleAt: DESKTOP_MIN_BREAK,
})`
  color: ${props => props.theme.primaryFontColorReverse};
  font-size: 1.25em;
  font-weight: 600;
`;

export const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: 0;
  @media (min-width: ${DESKTOP_MIN_BREAK}) {
    margin: 0 5px 0 0;
  }
`;

export const LabelWrapper = styled(ScreenReaderText).attrs({
  visibleAt: DESKTOP_MIN_BREAK,
})`
  color: ${props => props.theme.primaryFontColorReverse};
  display: flex;
  font-size: 1.2rem;
`;

const MOBILE_ICON_SIZE = 25;
const DESKTOP_ICON_SIZE = 20;

export const SecurityIcon = styled(SecurityIconBase).attrs(
  ({ theme, $mobileMode }) => ({
    height: $mobileMode ? MOBILE_ICON_SIZE : DESKTOP_ICON_SIZE,
    width: $mobileMode ? MOBILE_ICON_SIZE : DESKTOP_ICON_SIZE,
    fill: theme.primaryFontColorReverse,
  })
)``;

export const MenuButtonText = injectIntl(styled(NavToggleButtonText)`
  color: ${({ theme }) => theme.primaryFontColorReverse};
  text-transform: uppercase;
`);

const NavRightLinksBase = styled(ClearLink)`
  ${focusCssRule({ type: 'inset' })}

  border-radius: 0;
  display: flex;
  padding: 0 8px;
  font-size: 1.2rem;
  align-items: center;
  font-family: inherit;
  color: ${({ theme }) => theme.primaryFontColorReverse};
  @media (min-width: ${DESKTOP_MIN_BREAK}) {
    padding: 5px 10px;
  }
  @media (any-hover: hover) {
    &:hover,
    &:focus-visible {
      background-color: ${props => props.theme.navBackgroundHover};
    }
  }
`;

export const SecurityLink = styled(NavRightLinksBase).attrs(props => ({
  [GTM_DATA_ATTRIBUTE]: props.dataGtm,
}))``;

export const MenuIcon = styled(MenuIconBase).attrs(({ theme, mobileMode }) => ({
  height: mobileMode ? MOBILE_ICON_SIZE : DESKTOP_ICON_SIZE,
  width: mobileMode ? MOBILE_ICON_SIZE : DESKTOP_ICON_SIZE,
  fill: theme.primaryFontColorReverse,
}))`
  padding: 0 10px;
  @media (min-width: ${DESKTOP_MIN_BREAK}) {
    padding: 0;
  }
`;

export const MenuButtonLink = styled(ClearLink).attrs(({ $dataGtm }) => ({
  [GTM_DATA_ATTRIBUTE]: $dataGtm,
}))`
  ${focusCssRule({ type: 'inset' })}

  display: flex;
  background-color: ${props => props.theme.originColor};
  color: ${props => props.theme.primaryFontColorReverse};
  border: none;
  border-radius: 0;
  padding: 0 0.5em;
  align-items: center;
  @media (min-width: ${DESKTOP_MIN_BREAK}) {
    padding: 0 1em;
  }
  &:hover,
  &:focus,
  :active {
    background-color: ${props => props.theme.activeButtonHover};
  }
`;

export const StripeColumn = styled.div`
  display: none;
  @media (min-width: ${XSPHONE_BREAK}) {
    display: flex;
    flex: 1 1 auto;
    margin: 0 1em 0 0;
    width: 174px;
    height: 100%;
  }
`;
export const Stripe = styled(NavbarStripeSvg).attrs(props => ({
  fill: props.theme.primaryFontColorReverse,
}))`
  width: 100%;
  height: 100%;
`;
